<script setup>
import { useTemplateRef, ref, computed, watch, onMounted } from 'vue'
import { commons } from '@/lib/transformer'
import Mod from '@/components/mod.vue'
import View from './view.vue'
import { useDebounceFn } from '@vueuse/core'
import { extensions, extensionsImages, getExtension } from './view'
import { useUserStore } from '@/stores/user'
import { format } from 'date-fns'
import { storeToRefs } from 'pinia'
import { useApi } from '@/composables/api'
import config from '@/config'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const SCALE_STEP = 0.25
const SCALE_MIN = 0.25
const SCALE_MAX = 3

function clampScale(value) {
  if (value < SCALE_MIN) {
    return SCALE_MIN
  }
  if (value > SCALE_MAX) {
    return SCALE_MAX
  }
  return value
}

defineOptions({
  inheritAttrs: true,
})
const props = defineProps({
  upload: Object,
  versioning: Boolean,
  /** false — не трогать history (иначе back закрывает /tickets/new) */
  useHistory: {
    type: Boolean,
    default: true,
  },
  /** Прямая ссылка на просмотр (blob:/URL), без upload convert */
  link: {
    type: String,
    default: null,
  },
  /** URL для скачивания (если отличается от /download/{secure_filename}) */
  downloadUrl: {
    type: String,
    default: null,
  },
  /**
   * Кастомная смена версии (для документов вне Uploads).
   * async (versionItem) => ({ link, downloadUrl?, upload? })
   */
  resolveVersion: {
    type: Function,
    default: null,
  },
})

defineEmits(['hidden'])

const view = useTemplateRef('view')
const userStore = useUserStore()

function initialDisplayVersion() {
  if (props.versioning && props.upload?.versions?.length) {
    return (
      props.upload.versions.find(v => v.secure_filename == props.upload.secure_filename) ||
      props.upload.versions.find(v => v.version == props.upload.version) ||
      props.upload.versions[0]
    )
  }
  return {
    version: props.upload?.version ?? 0,
    secure_filename: props.upload?.secure_filename,
  }
}

const displayVersion = ref(initialDisplayVersion())
const uploadr = ref(props.upload)
const activeLink = ref(props.link)
const activeDownloadUrl = ref(props.downloadUrl)
const pageTotal = ref(1)
const pageCurrent = ref(1)
const scale = ref(1)
const rotation = ref(0)
const loading = ref(true)
const printing = ref(false)
const downloading = ref(false)

const fileOk = computed(
  () => !!activeLink.value || extensions.includes(getExtension(uploadr.value))
)
const isImage = computed(
  () => !activeLink.value && extensionsImages.includes(getExtension(uploadr.value))
)
const isPdfFile = computed(() => getExtension(uploadr.value) == 'pdf')

const sheetOrientation = ref(null)
const detectedOrientation = ref(null)
const viewFormat = computed(() =>
  sheetOrientation.value ? `pdf_${sheetOrientation.value}` : 'pdf'
)

// const allowView = userStore.can('docview_allow')
const allowView = true
const { user } = storeToRefs(userStore)

const CERT_LOGO_STAMP_DIVISIONS = [
  'analysis_certificates',
  'analysis_certificate_transfers',
]
const isCertificate = computed(() =>
  CERT_LOGO_STAMP_DIVISIONS.includes(uploadr.value?.division)
)

const { model, refresh } = useApi({
  url: computed(() => {
    const sec =
      displayVersion.value?.secure_filename ?? props.upload?.secure_filename ?? ''
    return sec ? `upload/${sec}` : 'upload'
  }),
  immediate: false,
  before: () =>
    !!(displayVersion.value?.secure_filename ?? props.upload?.secure_filename),
})

watch(
  () => props.link,
  link => {
    activeLink.value = link
  }
)
watch(
  () => props.downloadUrl,
  url => {
    activeDownloadUrl.value = url
  }
)
watch(
  () => props.upload,
  upload => {
    if (upload) uploadr.value = upload
  }
)

watch(
  model,
  model => {
    if (model && typeof model === 'object') {
      uploadr.value = model
    }
  },
  {
    deep: true,
  }
)

watch(displayVersion, async (next, prev) => {
  if (!next || next === 0) return
  if (prev && next?.secure_filename && next.secure_filename === prev.secure_filename) {
    return
  }
  // первичная установка текущей версии — документ уже загружен
  if (prev === 0 || prev == null) {
    const sameFile = next?.secure_filename
      ? next.secure_filename == uploadr.value?.secure_filename
      : next?.version == uploadr.value?.version
    if (sameFile) return
  }
  if (
    next?.secure_filename &&
    next.secure_filename == uploadr.value?.secure_filename &&
    !props.resolveVersion
  ) {
    return
  }
  if (props.resolveVersion && next?.version == uploadr.value?.version && activeLink.value) {
    return
  }
  scale.value = 1
  pageCurrent.value = 1
  pageTotal.value = 1
  loading.value = true

  if (props.resolveVersion) {
    try {
      const prevLink = activeLink.value
      const src = await props.resolveVersion(next)
      if (prevLink?.startsWith('blob:') && prevLink !== src?.link) {
        URL.revokeObjectURL(prevLink)
      }
      // без link — обычный convert/pdf по upload.secure_filename
      activeLink.value = src?.link ?? null
      activeDownloadUrl.value = src?.downloadUrl ?? null
      uploadr.value = {
        ...uploadr.value,
        ...(src?.upload || {}),
        version: next.version,
        versions: uploadr.value.versions,
      }
    } catch {
      loading.value = false
    }
    return
  }
  refresh()
})

onMounted(() => {
  if (props.versioning && props.upload?.versions?.length) {
    displayVersion.value =
      props.upload.versions.find(v => v.secure_filename == props.upload.secure_filename) ||
      props.upload.versions.find(v => v.version == props.upload.version) ||
      props.upload.versions[0]
  }
})

function onPage(current, total) {
  pageCurrent.value = current
  if (total !== undefined) {
    pageTotal.value = total
  }
}
function onLoaded(total, scale, orientation) {
  pageTotal.value = total
  if (orientation) {
    detectedOrientation.value = orientation
  }
  loading.value = false
}
function onRotate() {
  rotation.value = (rotation.value + 90) % 360
}
function onToggleOrientation() {
  loading.value = true
  const current = sheetOrientation.value || detectedOrientation.value
  sheetOrientation.value = current === 'landscape' ? 'portrait' : 'landscape'
}
function onScaleUp() {
  scale.value = clampScale(scale.value + SCALE_STEP)
}
function onScaleDown() {
  scale.value = clampScale(scale.value - SCALE_STEP)
}
function onWheelZoom(event) {
  if (!event.ctrlKey && !event.metaKey) {
    return
  }
  event.preventDefault()
  const step = event.deltaY < 0 ? SCALE_STEP : -SCALE_STEP
  scale.value = clampScale(scale.value + step)
}
const onJumpPage = useDebounceFn(event => {
  let n = Number(event.target.value)
  if (isNaN(n) || n > pageTotal.value) {
    n = pageCurrent.value
    event.target.value = n
    event.target.blur()
  }
  view.value.jumpTo(n)
}, 1000)

async function onPrint() {
  printing.value = true
  try {
    const filename = uploadr.value.filename || props.upload.filename
    if (isCertificate.value) {
      await view.value.print(150, filename, null, true)
    } else {
      await view.value.print(150, filename, addWatermark, false)
    }
  } finally {
    printing.value = false
  }
}

async function onDownload() {
  if (!activeDownloadUrl.value) return
  downloading.value = true
  try {
    const res = await fetch(activeDownloadUrl.value, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${user.value.access_token}` },
    })
    if (!res.ok) return
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = uploadr.value?.filename || 'document'
    a.click()
    URL.revokeObjectURL(url)
  } finally {
    downloading.value = false
  }
}

/**
 *
 * @param {HTMLCanvasElement} canvas
 */
function addWatermark(canvas) {
  const ctx = canvas.getContext('2d')
  const fontsize = 20
  const height = canvas.height
  const paddingY = 5
  let maxWidth = 0
  let { fio, username } = user.value
  let data = [
    t('requestedFrom', { service: `SOYKA:${String(config.VITE_SERVICE_SELF).toUpperCase()}` }),
    t('initiator', { user: fio || `$user:${username}` }),
    t('printed', { date: format(new Date(), 'dd.MM.Y HH:mm') }),
  ]

  if (props.versioning && displayVersion.value) {
    data = [
      ...data,
      t('versionStatus', {
        version: displayVersion.value.version,
        status: displayVersion.value.deleted ? t('outdated') : t('current'),
      }),
    ]
  }
  data.reverse()

  let minY = Infinity
  let maxY = -Infinity

  ctx.save()
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.font = `${fontsize}px sans-serif`
  ctx.globalAlpha = 0.4

  for (let i = 0; i < data.length; i++) {
    const line = data[i]
    const lineWidth = ctx.measureText(line).width
    if (lineWidth > maxWidth) {
      maxWidth = lineWidth
    }

    const lineOffset = fontsize * (i + 1)

    const baselineY = height - lineOffset
    const topY = baselineY - fontsize
    const bottomY = baselineY

    if (topY < minY) {
      minY = topY
    }
    if (bottomY > maxY) {
      maxY = bottomY
    }

    ctx.fillText(line, 20, baselineY)
  }

  ctx.globalAlpha = 1
  const rectTop = minY - paddingY
  const rectHeight = maxY - minY + paddingY * 2
  ctx.strokeRect(10, rectTop, maxWidth + 20, rectHeight)
  ctx.restore()
}
</script>
<template>
  <Mod
    @hidden="$emit('hidden')"
    size="full"
    ref="mod"
    :use-history="useHistory"
  >
    <template #header>
      <div class="d-flex gap-3 doc-view-topbar">
        <div
          class="hstack gap-3"
          v-if="pageTotal > 1 && fileOk"
        >
          <input
            type="number"
            min="1"
            class="form-control"
            :max="pageTotal"
            :value="pageCurrent"
            :disabled="!fileOk"
            @input="onJumpPage"
          />
          <span>/</span>
          <span>{{ pageTotal }}</span>
        </div>
        <div
          class="hstack gap-1"
          v-if="fileOk"
        >
          <button
            class="btn btn-secondary"
            type="button"
            @click="onScaleUp"
          >
            <i class="bi bi-zoom-in" />
          </button>
          <span>{{ Math.round(scale * 100) }}%</span>
          <button
            class="btn btn-secondary"
            type="button"
            @click="onScaleDown"
          >
            <i class="bi bi-zoom-out" />
          </button>
        </div>
        <button
          class="btn btn-secondary"
          type="button"
          @click="scale = clampScale(1)"
          v-if="scale != 1"
        >
          <i class="bi bi-arrows-fullscrenn" /> 100%
        </button>
        <button
          class="btn btn-secondary"
          type="button"
          v-if="fileOk && !isImage"
          title="Повернуть страницу на 90°"
          @click="onRotate"
        >
          <i class="bi bi-arrow-clockwise" />
        </button>
        <button
          class="btn btn-secondary"
          type="button"
          v-if="fileOk && !isImage && !isPdfFile"
          :title="
            (sheetOrientation || detectedOrientation) === 'landscape'
              ? 'Ориентация листа: альбомная — переключить на книжную'
              : 'Ориентация листа: книжная/альбомная'
          "
          @click="onToggleOrientation"
        >
          <i class="bi bi-aspect-ratio" />
        </button>
        <button
          class="btn btn-secondary"
          type="button"
          v-if="allowView && fileOk"
          :disabled="printing || loading"
          @click="onPrint"
        >
          <i class="bi bi-printer-fill" />
        </button>
        <button
          v-if="allowView && activeDownloadUrl"
          type="button"
          class="btn btn-secondary"
          :disabled="downloading"
          @click="onDownload"
        >
          <i class="bi bi-cloud-download"></i>
        </button>
        <a
          v-else-if="allowView && uploadr.secure_filename"
          :href="`/api/v1.0/download/${uploadr.secure_filename}`"
          :download="uploadr.filename"
          class="btn btn-secondary"
        >
          <i class="bi bi-cloud-download"></i>
        </a>
        <slot name="toolbar" :version="displayVersion" :upload="uploadr" />
      </div>
    </template>
    <div class="doc-view-ui" @wheel="onWheelZoom">
      <View
        :upload="uploadr"
        :link="activeLink"
        :scale="scale"
        :rotation="rotation"
        :format="viewFormat"
        @page="onPage"
        @loaded="onLoaded"
        ref="view"
      />
      <div class="overflow-y-auto doc-view-sidebar">
        <h3>{{ t('author') }}</h3>
        <dl>
          <template v-if="uploadr.author?.email">
            <dt>Email</dt>
            <dd>{{ uploadr.author.email }}</dd>
          </template>
          <dt>{{ t('common.fio') }}</dt>
          <dd>{{ uploadr.author?.fio || uploadr.author?.username || '—' }}</dd>
        </dl>
        <h3>{{ t('file') }}</h3>
        <dl>
          <dt>{{ t('file') }}</dt>
          <dd>{{ uploadr.filename }}</dd>

          <dt>{{ t('uploaded') }}</dt>
          <dd>{{ uploadr.uploaded_at ? commons.dateTimeLocal(uploadr.uploaded_at) : '—' }}</dd>

          <template v-if="fileOk && !isImage">
            <dt>{{ t('totalPages') }}</dt>
            <dd>{{ pageTotal }}</dd>
          </template>

          <template v-if="versioning && uploadr.versions?.length">
            <dt>{{ t('version') }}</dt>
            <dd>
              <select
                class="form-select"
                v-model="displayVersion"
              >
                <option
                  v-for="v of uploadr.versions"
                  :key="v.secure_filename || v.version"
                  :value="v"
                >
                  {{ t('versionNumber', { version: v.version }) }}
                </option>
              </select>
            </dd>
          </template>
        </dl>
      </div>
    </div>
  </Mod>
</template>

<i18n lang="json">
{
  "ru": {
    "requestedFrom": "Запрошено из: {service}",
    "initiator": "Инициатор: {user}, AstraZeneca",
    "printed": "Распечатано: {date}",
    "versionStatus": "Версия: {version} / {status}",
    "outdated": "неактуальная",
    "current": "актуальная",
    "author": "Автор",
    "file": "Файл",
    "uploaded": "Загружен",
    "totalPages": "Всего страниц",
    "version": "Версия",
    "versionNumber": "Версия {version}"
  },
  "en": {
    "requestedFrom": "Requested from: {service}",
    "initiator": "Initiator: {user}, AstraZeneca",
    "printed": "Printed: {date}",
    "versionStatus": "Version: {version} / {status}",
    "outdated": "outdated",
    "current": "current",
    "author": "Author",
    "file": "File",
    "uploaded": "Uploaded",
    "totalPages": "Total pages",
    "version": "Version",
    "versionNumber": "Version {version}"
  }
}
</i18n>
